import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
    Menu,
    X,
    FileText,
    User,
    Heart,
    ShoppingCart,
    Search,
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    Home,
    LayoutGrid,
    List,
    Package,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { db, auth } from '../utils/firebase';
import { collection, getDocs, query, orderBy, where, onSnapshot, limit } from 'firebase/firestore';
import { execute, field, stringContains, toLower } from 'firebase/firestore/pipelines';

const Navbar = ({ cartCount, myListCount: propMyListCount = 0, searchQuery, setSearchQuery, selectedCategory, setSelectedCategory, selectedSubcategory, setSelectedSubcategory, onCartClick, onStoreLocationsClick, onAuthClick, onFlyersClick }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const searchInputRef = useRef(null);
    const searchContainerRef = useRef(null);
    const mobileSearchInputRef = useRef(null);
    const mobileSearchContainerRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [wishlistCount, setWishlistCount] = useState(0);

    const myListCount = wishlistCount;

    // Firestore Pipeline prefix autocomplete — "cli" matches "cling wrap"
    useEffect(() => {
        const queryStr = searchQuery.trim();
        if (queryStr === '') {
            setSuggestions([]);
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                const lowerQuery = queryStr.toLowerCase();

                // Contains match: "one" → "product one", "stone", "bone broth", etc.
                const snapshot = await execute(
                    db.pipeline()
                        .collection('products')
                        .where(stringContains(toLower(field('productName')), lowerQuery))
                        .where(field('status').notEqual(false))
                        .limit(6)
                );
                const pipelineResults = snapshot.results;

                const hits = pipelineResults.map(r => {
                    const data = r.data();
                    const docId = r.id || '';
                    const displayCode = data.productCodeLetters && data.productCode
                        ? `${data.productCodeLetters}${data.productCode}`
                        : (data.code || '');
                    return {
                        id: docId,
                        title: data.productName || data.title || '',
                        code: displayCode,
                        image: data.image || (data.images?.[0]) || '/product-placeholder.png',
                        brand: data.brandName || data.brand || '',
                        type: data.type_name || data.type || '',
                        category: data.categoryName || data.category || '',
                        subcategory: data.subCategoryName || data.subcategory || '',
                        ...data,
                    };
                });

                setSuggestions(hits);
            } catch (e) {
                console.error("Error loading search suggestions from Firebase:", e);
                setSuggestions([]);
            }
        }, 300);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [searchQuery]);

    // Calculate display price for autocomplete search suggestion
    const getDisplayPrice = (product) => {
        const userSchemeId = user?.scheme_id || null;
        if (!userSchemeId) return null;

        const schemePricing = product.pricing?.find(p => p.scheme_id === userSchemeId);
        if (!schemePricing) return null;

        if (schemePricing.tiers?.[0]) {
            return schemePricing.tiers[0].price;
        }
        return null;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutsideDesktop = !searchContainerRef.current || !searchContainerRef.current.contains(event.target);
            const isOutsideMobile = !mobileSearchContainerRef.current || !mobileSearchContainerRef.current.contains(event.target);
            if (isOutsideDesktop && isOutsideMobile) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const parsed = JSON.parse(storedUser);
                if (parsed && parsed.isLoggedIn) setUser(parsed);
                else setUser(null);
            } catch (e) {
                setUser(null);
            }
        }

        const handleUserLogin = () => {
            const updatedUser = localStorage.getItem('user');
            if (updatedUser) {
                try {
                    const parsed = JSON.parse(updatedUser);
                    if (parsed && parsed.isLoggedIn) setUser(parsed);
                    else setUser(null);
                } catch (e) {
                    setUser(null);
                }
            } else {
                setUser(null);
            }
        };

        window.addEventListener('user-login', handleUserLogin);
        return () => window.removeEventListener('user-login', handleUserLogin);
    }, []);

    // Update dropdown position whenever the query changes
    useEffect(() => {
        const activeContainer = (searchContainerRef.current && searchContainerRef.current.offsetParent !== null)
            ? searchContainerRef.current
            : mobileSearchContainerRef.current;
        if (activeContainer) {
            const rect = activeContainer.getBoundingClientRect();
            setDropdownPos({
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX,
                width: Math.max(rect.width, 280),
            });
        }
    }, [searchQuery]);

    const closeSearch = () => {
        setSearchQuery("");
        setShowSuggestions(false);
    };
    const handleNavigateToFlyerDeals = () => {
        navigate("/flyerdeals");
    };
    const handleNavigateToMyLists = (tab = 'easy_orders') => {
        navigate(`/my-lists${tab ? `?tab=${tab}` : ''}`);
    };
    const handleNavigateToInventory = () => {
        navigate("/inventory");
    };

    // Category Bar States & Functions
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [navTypes, setNavTypes] = useState([]);

    useEffect(() => {
        let unsubscribeWishlist = null;

        const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
            if (unsubscribeWishlist) {
                unsubscribeWishlist();
                unsubscribeWishlist = null;
            }

            if (currentUser) {
                const q = query(
                    collection(db, 'products'),
                    where('wishlistIds', 'array-contains', currentUser.uid), limit(11)
                );
                unsubscribeWishlist = onSnapshot(q, (snapshot) => {
                    setWishlistCount(snapshot.size);
                }, (e) => {
                    console.error('Error loading wishlist count:', e);
                });
            } else {
                setWishlistCount(0);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeWishlist) unsubscribeWishlist();
        };
    }, []);

    // Fetch types hierarchy from Firestore for the Navbar mega-menu
    useEffect(() => {
        const fetchNavTypes = async () => {
            try {
                const [typesSnap, catsSnap, subsSnap] = await Promise.all([
                    getDocs(query(collection(db, 'types'), orderBy('order'))),
                    getDocs(query(collection(db, 'categories'), orderBy('order'))),
                    getDocs(query(collection(db, 'sub_categories'), orderBy('order'))),
                ]);

                const allCats = catsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                const allSubs = subsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                const loaded = typesSnap.docs.map(typeDoc => {
                    const typeId = typeDoc.id;
                    const typeData = typeDoc.data();

                    const cats = allCats
                        .filter(c => c.type_id === typeId && c.status !== false)
                        .map(cat => ({
                            id: cat.id,
                            name: cat.title || cat.name || cat.id,
                            subcategories: allSubs
                                .filter(s => s.category_id === cat.id && s.status !== false)
                                .map(s => s.title || s.name || s.id),
                        }));

                    return {
                        id: typeId,
                        name: typeData.title || typeData.name || typeId,
                        categories: cats,
                    };
                });

                setNavTypes(loaded);
            } catch (e) {
                console.warn('Could not fetch nav types:', e);
            }
        };
        fetchNavTypes();
    }, []);

    const [dropdownLeft, setDropdownLeft] = useState(0);
    const filterBarRef = useRef(null);
    const scrollRef = useRef(null);
    const timeoutRef = useRef(null);
    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (scrollRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
                setShowLeftScroll(scrollLeft > 5);
                setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 5);
            }
        };
        const timer = setTimeout(checkScroll, 150);
        window.addEventListener('resize', checkScroll);
        return () => {
            clearTimeout(timer);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            window.removeEventListener('resize', checkScroll);
        };
    }, []);

    // Auto-close category hover dropdown on outside touch/click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
                setHoveredCategory(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const calculateDropdownPosition = (catName, currentTarget) => {
        if (!filterBarRef.current || !currentTarget) return;
        const buttonRect = currentTarget.getBoundingClientRect();
        const containerRect = filterBarRef.current.getBoundingClientRect();

        const hoveredType = navTypes.find(t => t.name === catName);
        const columnsCount = hoveredType?.categories?.length || 1;
        const assumedWidth = Math.min((columnsCount * 175) + ((columnsCount - 1) * 32) + 48, window.innerWidth - 32);

        const relativeLeft = buttonRect.left - containerRect.left + (buttonRect.width / 2) - (assumedWidth / 2);

        const minLeft = 16 - containerRect.left;
        const maxLeft = window.innerWidth - 16 - assumedWidth - containerRect.left;

        const finalLeft = Math.max(minLeft, Math.min(maxLeft, relativeLeft));
        setDropdownLeft(finalLeft);
    };

    const handleCategoryMouseEnter = (catName, event) => {
        if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
            return;
        }
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        calculateDropdownPosition(catName, event.currentTarget);
        setHoveredCategory(catName);
    };

    const handleCategoryClick = (catName, event) => {
        event.stopPropagation();
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        if (hoveredCategory === catName) {
            setHoveredCategory(null);
        } else {
            calculateDropdownPosition(catName, event.currentTarget);
            setHoveredCategory(catName);
        }
    };

    const isTouchDevice = () => window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

    const handleCategoryMouseLeave = () => {
        if (isTouchDevice()) return;
        timeoutRef.current = setTimeout(() => setHoveredCategory(null), 150);
    };

    const handleDropdownMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    const handleDropdownMouseLeave = () => {
        if (isTouchDevice()) return;
        setHoveredCategory(null);
    };

    const lastScrollLeftRef = useRef(0);

    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setShowLeftScroll(scrollLeft > 5);
            setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 5);

            // Only close if user manually scrolled horizontally by more than 20px
            if (Math.abs(scrollLeft - lastScrollLeftRef.current) > 20) {
                setHoveredCategory(null);
                lastScrollLeftRef.current = scrollLeft;
            }
        }
    };

    const scrollCategories = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = 200;
            scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
        }
    };

    const handleSubcategoryClick = (categoryName, subcategoryName) => {
        setHoveredCategory(null);
        const params = new URLSearchParams();
        if (categoryName) params.set('category', categoryName);
        if (subcategoryName) params.set('subcategory', subcategoryName);
        navigate(`/products?${params.toString()}`);
    };

    const clearFilters = () => {
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSearchQuery("");
        navigate("/products");
    };

    return (
        <>
            <header className="sticky top-0 z-50 w-full border-b border-[#154734] bg-[#154734]/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-all">

                {/* Top Emerald Ambient Line */}
                <div className="h-0.5 w-full bg-gradient-to-r from-[#154734] via-[#FFF7EC] to-[#154734]" />

                <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8">
                    {/* ================= DESKTOP NAVBAR ================= */}
                    <div className="hidden lg:flex lg:h-[88px] lg:items-center lg:justify-between lg:gap-6">

                        {/* LEFT: Logo */}
                        <div className="flex items-center gap-6 shrink-0">
                            <Link
                                to="/"
                                className="group relative flex items-center shrink-0 transition-transform duration-300 hover:scale-105"
                            >
                                <img
                                    src="/logo.png"
                                    alt="ChefCo Hospitality Partners"
                                    className="h-24 md:h-28 lg:h-32 w-auto object-contain py-1 ml-2"
                                />
                            </Link>
                        </div>

                        {/* CENTER: SEARCH INPUT BAR */}
                        <div className="flex-1 max-w-xl mx-auto">
                            <div ref={searchContainerRef} className="relative w-full">
                                <div className="flex items-center bg-white border border-[#E6E6E6] focus-within:border-[#154734] focus-within:ring-2 focus-within:ring-[#154734]/20 rounded-2xl px-4 h-11 gap-3 shadow-inner transition-all duration-300">
                                    <Search className="w-4 h-4 text-[#154734] shrink-0" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder="Search wholesale products, packaging, produce..."
                                        value={searchQuery}
                                        onFocus={() => setShowSuggestions(true)}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setShowSuggestions(true);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Escape") {
                                                setShowSuggestions(false);
                                            }
                                            if (e.key === "Enter" && searchQuery.trim()) {
                                                setShowSuggestions(false);
                                                navigate(`/products?q=${encodeURIComponent(searchQuery)}`);
                                            }
                                        }}
                                        className="bg-transparent border-none outline-none text-[#154734] text-xs sm:text-sm font-semibold placeholder-slate-500 w-full"
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={closeSearch}
                                            className="shrink-0 text-slate-400 hover:text-white transition-colors border-none bg-transparent cursor-pointer"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Search Autocomplete Suggestions Dropdown */}
                            {searchQuery.trim() !== '' && showSuggestions && suggestions.length > 0 && createPortal(
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: dropdownPos.top,
                                        left: dropdownPos.left,
                                        width: dropdownPos.width,
                                        zIndex: 9999,
                                    }}
                                    className="bg-[#FFFFFF] border border-[#DDDDDD] rounded-2xl shadow-2xl max-h-80 overflow-y-auto divide-y divide-[#F5F5F5] text-[#154734]"
                                >
                                    {suggestions.map((product, idx) => {
                                        const displayPrice = getDisplayPrice(product);
                                        return (
                                            <div
                                                key={idx}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    setShowSuggestions(false);
                                                    navigate(`/product/${product.id}`);
                                                }}
                                                className="flex items-center justify-between p-3 hover:bg-[#F5F5F5] transition cursor-pointer"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={product.image || '/product-placeholder.png'}
                                                        alt={product.title}
                                                        className="w-9 h-9 object-contain rounded-lg bg-[#FFFFFF] border border-[#DDDDDD] p-1 shrink-0"
                                                    />
                                                    <div className="min-w-0">
                                                        <div className="text-xs font-bold text-[#154734] truncate max-w-[260px]">{product.title}</div>
                                                        <div className="text-[10px] text-slate-500 font-semibold mt-0.5">{product.category}</div>
                                                    </div>
                                                </div>
                                                {displayPrice !== null && (
                                                    <div className="text-xs font-black text-[#154734] ml-2">
                                                        ₹{typeof displayPrice === 'number' ? (displayPrice % 1 === 0 ? displayPrice : displayPrice.toFixed(2)) : displayPrice}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    <button
                                        onMouseDown={(e) => { e.preventDefault(); setShowSuggestions(false); navigate(`/products?q=${encodeURIComponent(searchQuery)}`); }}
                                        className="flex items-center justify-between w-full p-3 text-xs font-bold text-[#154734] bg-[#FFFFFF] hover:bg-[#F5F5F5] transition border-none cursor-pointer text-left"
                                    >
                                        <span>Show all results for "{searchQuery}"</span>
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>,
                                document.body
                            )}
                        </div>

                        {/* RIGHT: ICON ACTION SECTIONS WITH CURSOR HOVER ANIMATION */}
                        <div className="flex items-center gap-1.5 shrink-0">

                            {/* 1. Cart Section */}
                            <Link
                                to="/cart"
                                className="group relative flex flex-col items-center justify-center gap-1 py-2 px-3.5 rounded-2xl bg-transparent border-none cursor-pointer no-underline transition-all duration-300 hover:-translate-y-1.5 hover:scale-105 active:scale-95"
                            >
                                {/* Hover Pill Glow Backdrop */}
                                <div className="absolute inset-0 bg-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 border border-[#FFFFFF]/20" />

                                <div className="relative z-10">
                                    <ShoppingCart className="w-5 h-5 text-white transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110 group-hover:text-white" />
                                    {cartCount > 0 && (
                                        <span className="absolute -top-2 -right-2.5 text-[9px] font-black bg-[#FFF7EC] text-[#154734] w-4 h-4 rounded-full flex items-center justify-center shadow-sm animate-pulse">
                                            {cartCount}
                                        </span>
                                    )}
                                </div>
                                <span className="relative z-10 text-[11px] font-extrabold text-white whitespace-nowrap transition-colors group-hover:text-white">
                                    My Cart
                                </span>
                            </Link>

                            <div className="w-px h-6 bg-white/10 mx-0.5" />

                            {/* 2. Departments Section */}
                            <Link
                                to="/departments"
                                className="group relative flex flex-col items-center justify-center gap-1 py-2 px-3.5 rounded-2xl bg-transparent border-none cursor-pointer no-underline transition-all duration-300 hover:-translate-y-1.5 hover:scale-105 active:scale-95"
                            >
                                <div className="absolute inset-0 bg-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 border border-[#FFFFFF]/20" />

                                <LayoutGrid className="relative z-10 w-5 h-5 text-white transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110 group-hover:text-white" />
                                <span className="relative z-10 text-[11px] font-extrabold text-white whitespace-nowrap transition-colors group-hover:text-white">
                                    Departments
                                </span>
                            </Link>

                            <div className="w-px h-6 bg-white/10 mx-0.5" />

                            {/* 3. Flyers & Deals Section */}
                            <button
                                className="group relative flex flex-col items-center justify-center gap-1 py-2 px-3.5 rounded-2xl bg-transparent border-none cursor-pointer transition-all duration-300 hover:-translate-y-1.5 hover:scale-105 active:scale-95"
                                onClick={handleNavigateToFlyerDeals}
                            >
                                <div className="absolute inset-0 bg-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 border border-[#FFFFFF]/20" />

                                <FileText className="relative z-10 w-5 h-5 text-white transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:text-white" />
                                <span className="relative z-10 text-[11px] font-extrabold text-white whitespace-nowrap transition-colors group-hover:text-white">
                                    Flyers & Deals
                                </span>
                            </button>

                            <div className="w-px h-6 bg-white/10 mx-0.5" />

                            {/* 4. Favorites Section */}
                            <button
                                className="group relative flex flex-col items-center justify-center gap-1 py-2 px-3.5 rounded-2xl bg-transparent border-none cursor-pointer transition-all duration-300 hover:-translate-y-1.5 hover:scale-105 active:scale-95"
                                onClick={() => handleNavigateToMyLists('wishlist')}
                            >
                                <div className="absolute inset-0 bg-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 border border-[#FFFFFF]/20" />

                                <div className="relative z-10">
                                    <Heart className="w-5 h-5 text-white transition-all duration-300 group-hover:scale-110 group-hover:text-white group-hover:fill-white" />
                                    {myListCount > 0 && (
                                        <span className="absolute -top-2 -right-2.5 text-[9px] font-black bg-[#FFF7EC] text-[#154734] w-4 h-4 rounded-full flex items-center justify-center shadow-sm">
                                            {myListCount > 10 ? '10+' : myListCount}
                                        </span>
                                    )}
                                </div>
                                <span className="relative z-10 text-[11px] font-extrabold text-white whitespace-nowrap transition-colors group-hover:text-white">
                                    Favorites
                                </span>
                            </button>

                            <div className="w-px h-6 bg-white/10 mx-0.5" />

                            {/* 5. My List Section */}
                            <button
                                className="group relative flex flex-col items-center justify-center gap-1 py-2 px-3.5 rounded-2xl bg-transparent border-none cursor-pointer transition-all duration-300 hover:-translate-y-1.5 hover:scale-105 active:scale-95"
                                onClick={() => handleNavigateToMyLists('easy_orders')}
                            >
                                <div className="absolute inset-0 bg-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 border border-[#FFFFFF]/20" />

                                <List className="relative z-10 w-5 h-5 text-white transition-transform duration-300 group-hover:scale-110 group-hover:text-white" />
                                <span className="relative z-10 text-[11px] font-extrabold text-white whitespace-nowrap transition-colors group-hover:text-white">
                                    My List
                                </span>
                            </button>

                            <div className="w-px h-6 bg-white/10 mx-0.5" />

                            {/* 6. Inventory Section */}
                            <button
                                className="group relative flex flex-col items-center justify-center gap-1 py-2 px-3.5 rounded-2xl bg-transparent border-none cursor-pointer transition-all duration-300 hover:-translate-y-1.5 hover:scale-105 active:scale-95"
                                onClick={handleNavigateToInventory}
                            >
                                <div className="absolute inset-0 bg-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 border border-[#FFFFFF]/20" />

                                <Package className="relative z-10 w-5 h-5 text-white transition-transform duration-300 group-hover:scale-110 group-hover:text-white" />
                                <span className="relative z-10 text-[11px] font-extrabold text-white whitespace-nowrap transition-colors group-hover:text-white">
                                    Inventory
                                </span>
                            </button>

                            <div className="w-px h-6 bg-white/10 mx-0.5" />

                            {/* 5. User Profile / Auth Section */}
                            {user ? (
                                <Link
                                    to="/profile"
                                    className="group relative flex flex-col items-center justify-center gap-1 py-2 px-3.5 rounded-2xl bg-transparent border-none cursor-pointer no-underline transition-all duration-300 hover:-translate-y-1.5 hover:scale-105 active:scale-95"
                                >
                                    <div className="absolute inset-0 bg-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 border border-[#FFFFFF]/20" />

                                    <div className="relative z-10 w-7 h-7 rounded-full bg-[#FFF7EC] border border-[#FFF7EC]/40 flex items-center justify-center transition-transform group-hover:scale-110 shadow-xs">
                                        <User className="w-4 h-4 text-[#2C3E2B]" />
                                    </div>
                                    <span className="relative z-10 text-[11px] font-extrabold text-white whitespace-nowrap transition-colors group-hover:text-white truncate max-w-[65px]">
                                        {user.fullName || user.name}
                                    </span>
                                </Link>
                            ) : (
                                <button
                                    onClick={onAuthClick}
                                    className="group relative flex flex-col items-center justify-center gap-1 py-2 px-3.5 rounded-2xl bg-transparent border-none cursor-pointer transition-all duration-300 hover:-translate-y-1.5 hover:scale-105 active:scale-95"
                                >
                                    <div className="absolute inset-0 bg-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 border border-[#FFFFFF]/20" />

                                    <User className="relative z-10 w-5 h-5 text-white transition-all duration-300 group-hover:scale-110 group-hover:text-white" />
                                    <span className="relative z-10 text-[11px] font-extrabold text-white whitespace-nowrap transition-colors group-hover:text-white">
                                        Login/Register
                                    </span>
                                </button>
                            )}

                        </div>
                    </div>

                    {/* ================= MOBILE NAVBAR ================= */}
                    <div className="flex lg:hidden items-center justify-between h-[70px] gap-2 px-1">
                        <Link to="/" className="flex items-center shrink-0">
                            <img
                                src="/logo.png"
                                alt="ChefCo Hospitality Partners"
                                className="h-14 sm:h-16 w-auto object-contain"
                            />
                        </Link>

                        {/* SEARCH INPUT BAR - LEFT OF CART ICON */}
                        <div className="flex-1 min-w-0 mx-1 max-w-[220px]">
                            <div ref={mobileSearchContainerRef} className="relative w-full">
                                <div className="flex items-center bg-white border border-[#E6E6E6] focus-within:border-[#154734] focus-within:ring-2 focus-within:ring-[#154734]/20 rounded-xl px-2.5 h-9 gap-1.5 shadow-inner transition-all duration-300">
                                    <Search className="w-3.5 h-3.5 text-[#154734] shrink-0" />
                                    <input
                                        ref={mobileSearchInputRef}
                                        type="text"
                                        placeholder="Search products..."
                                        value={searchQuery}
                                        onFocus={() => setShowSuggestions(true)}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setShowSuggestions(true);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Escape") {
                                                setShowSuggestions(false);
                                            }
                                            if (e.key === "Enter" && searchQuery.trim()) {
                                                setShowSuggestions(false);
                                                navigate(`/products?q=${encodeURIComponent(searchQuery)}`);
                                            }
                                        }}
                                        className="bg-transparent border-none outline-none text-[#154734] text-xs font-semibold placeholder-slate-400 w-full min-w-0"
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={closeSearch}
                                            className="shrink-0 text-slate-400 hover:text-[#154734] transition-colors border-none bg-transparent cursor-pointer p-0"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                            <Link
                                to="/cart"
                                className="relative w-9 h-9 rounded-xl bg-transparent border border-white/20 text-white flex items-center justify-center cursor-pointer transition-colors hover:border-white/50 hover:bg-white/5"
                            >
                                <ShoppingCart className="w-4 h-4 text-white" />
                                {cartCount > 0 && (
                                    <span className="absolute -top-1 -right-1 text-[9px] font-black bg-[#FFF7EC] text-[#154734] w-4 h-4 rounded-full flex items-center justify-center">
                                        {cartCount}
                                    </span>
                                )}
                            </Link>

                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="w-9 h-9 rounded-xl bg-transparent border border-white/20 text-white flex items-center justify-center cursor-pointer transition-colors hover:border-white/50 hover:bg-white/5"
                            >
                                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ================= MOBILE MENU DRAWER ================= */}
                {mobileMenuOpen && (
                    <div className="lg:hidden bg-[#154734]/95 backdrop-blur-2xl border-t border-[#154734] shadow-2xl">
                        <div className="px-5 py-5 space-y-3">

                            <button
                                onClick={() => { handleNavigateToFlyerDeals(); setMobileMenuOpen(false); }}
                                className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-white font-semibold hover:bg-[#154734] transition-all border-none bg-transparent cursor-pointer text-left"
                            >
                                <FileText className="w-4 h-4 text-[#154734]" />
                                <span>Flyers & Deals</span>
                            </button>

                            {user ? (
                                <button
                                    onClick={() => { navigate("/profile"); setMobileMenuOpen(false); }}
                                    className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-white font-semibold hover:bg-[#154734] transition-all border-none bg-transparent cursor-pointer text-left"
                                >
                                    <div className="w-6 h-6 rounded-full bg-[#FFF7EC] flex items-center justify-center shrink-0">
                                        <User className="w-3.5 h-3.5 text-[#154734]" />
                                    </div>
                                    <span>Profile</span>
                                </button>
                            ) : (
                                <button
                                    onClick={() => { onAuthClick && onAuthClick(); setMobileMenuOpen(false); }}
                                    className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-white font-semibold hover:bg-[#154734] transition-all border-none bg-transparent cursor-pointer text-left"
                                >
                                    <User className="w-4 h-4 text-[#154734]" />
                                    <span>Login / Register</span>
                                </button>
                            )}

                            <button
                                onClick={() => { handleNavigateToMyLists('wishlist'); setMobileMenuOpen(false); }}
                                className="flex items-center justify-between w-full px-4 py-3 rounded-2xl text-white font-semibold hover:bg-[#154734] transition-all border-none bg-transparent cursor-pointer"
                            >
                                <div className="flex items-center gap-3">
                                    <Heart className="w-4 h-4 text-red-500" />
                                    <span>Favorites</span>
                                </div>
                                {myListCount > 0 && <span className="text-xs font-black bg-[#154734] text-black px-2 py-0.5 rounded-full">{myListCount > 10 ? '10+' : myListCount}</span>}
                            </button>

                            <button
                                onClick={() => { handleNavigateToMyLists('easy_orders'); setMobileMenuOpen(false); }}
                                className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-white font-semibold hover:bg-[#154734] transition-all border-none bg-transparent cursor-pointer text-left"
                            >
                                <List className="w-4 h-4 text-white" />
                                <span>My List</span>
                            </button>

                            <button
                                onClick={() => { handleNavigateToInventory(); setMobileMenuOpen(false); }}
                                className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-white font-semibold hover:bg-[#154734] transition-all border-none bg-transparent cursor-pointer text-left"
                            >
                                <Package className="w-4 h-4 text-[#154734]" />
                                <span>Inventory</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* ================= CATEGORY BAR (LIGHT ORANGE BAR) ================= */}
                <div className="border-t border-[#E6E6E6] bg-[#FFF7EC] shadow-[0_10px_30px_rgba(10,46,34,0.05)] relative z-40">
                    <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8">
                        <div ref={filterBarRef} className="flex items-center gap-2.5 py-2.5 overflow-visible relative">
                            {/* All Products Button */}
                            <button
                                onClick={() => {
                                    setHoveredCategory(null);
                                    clearFilters();
                                }}
                                className={`group relative flex items-center gap-2 px-4 py-2 rounded-2xl text-xs sm:text-sm font-extrabold tracking-wide transition-all duration-300 cursor-pointer border-none ${!selectedCategory && !selectedSubcategory
                                    ? 'bg-[#154734] text-white shadow-md scale-105'
                                    : 'bg-[#154734]/5 hover:bg-[#154734]/10 text-[#154734] hover:text-[#0A2E22] border border-[#154734]/10 hover:border-[#154734]/20 hover:-translate-y-1 hover:scale-105 active:scale-95'
                                    }`}
                            >
                                <span>All Products</span>
                            </button>

                            <span className="text-[#154734]/20 font-light mx-1">|</span>

                            {showLeftScroll && (
                                <button
                                    className="w-8 h-8 rounded-full bg-white text-[#154734] border border-[#E6E6E6] hover:bg-[#FFF7EC] transition-all flex items-center justify-center shrink-0 cursor-pointer shadow-sm"
                                    onClick={() => scrollCategories('left')}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                            )}

                            {/* Category List Scroll Wrapper */}
                            <div ref={scrollRef} className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] py-1 flex-1 touch-pan-x" style={{ WebkitOverflowScrolling: 'touch' }} onScroll={handleScroll}>
                                {navTypes.map((type, idx) => {
                                    const isOpen = hoveredCategory === type.name;
                                    const isSelected = selectedCategory === type.name;
                                    return (
                                        <div
                                            key={idx}
                                            className="relative shrink-0"
                                            onMouseEnter={(e) => handleCategoryMouseEnter(type.name, e)}
                                            onMouseLeave={handleCategoryMouseLeave}
                                        >
                                            <button
                                                onClick={(e) => handleCategoryClick(type.name, e)}
                                                className={`group relative flex items-center gap-2 px-4 py-2 rounded-2xl text-xs sm:text-sm font-extrabold tracking-wide transition-all duration-300 cursor-pointer border ${isSelected
                                                    ? 'bg-[#154734] text-white border-transparent shadow-md scale-105'
                                                    : 'bg-[#154734]/5 hover:bg-[#154734]/10 text-[#154734] hover:text-[#0A2E22] border-[#154734]/10 hover:border-[#154734]/20 hover:-translate-y-1 hover:scale-105 active:scale-95 shadow-sm'
                                                    }`}
                                            >
                                                <span className="relative z-10">{type.name}</span>
                                                <ChevronDown className={`relative z-10 w-3.5 h-3.5 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'group-hover:rotate-180'}`} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {showRightScroll && (
                                <button
                                    className="w-8 h-8 rounded-full bg-white text-[#154734] border border-[#E6E6E6] hover:bg-[#FFF7EC] transition-all flex items-center justify-center shrink-0 cursor-pointer shadow-sm"
                                    onClick={() => scrollCategories('right')}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            )}
                            {/* Mega-menu Dropdown — rendered at filterBar level, outside the scroll container */}
                            {hoveredCategory && (
                                <div
                                    className="absolute top-full pt-2 z-[200] animate-in fade-in slide-in-from-top-2 duration-150"
                                    style={{
                                        left: `${dropdownLeft}px`,
                                        // Cap width so it never bleeds off-screen
                                        maxWidth: 'calc(100% - 16px)',
                                    }}
                                    onMouseEnter={handleDropdownMouseEnter}
                                    onMouseLeave={handleDropdownMouseLeave}
                                >
                                    <div
                                        className="bg-[#FFFFFF] rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.18)] border border-[#E0E0E0] p-4 sm:p-5 text-[#333333] max-h-[75vh] md:max-h-[60vh] overflow-y-auto overscroll-contain touch-pan-y"
                                        style={{ width: 'max-content', maxWidth: 'calc(100vw - 24px)', WebkitOverflowScrolling: 'touch' }}
                                    >
                                        <div className="flex flex-wrap gap-x-6 gap-y-3">
                                            {navTypes.find(t => t.name === hoveredCategory)?.categories?.map((cat, cIdx) => (
                                                <div key={cIdx} className="flex flex-col min-w-[150px]">
                                                    <h5
                                                        className="font-bold text-[#1F4D24] text-[13px] mb-1.5 pb-1.5 border-b border-[#E0E0E0] inline-flex items-center gap-2 cursor-pointer hover:text-white transition-all group w-full"
                                                        onClick={() => {
                                                            setHoveredCategory(null);
                                                            navigate(`/products?category=${encodeURIComponent(cat.name)}`);
                                                        }}
                                                    >
                                                        <span>{cat.name}</span>
                                                        <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-[#1F4D24] ml-auto" />
                                                    </h5>
                                                    <div className="flex flex-col gap-0.5 mt-0.5">
                                                        {cat.subcategories.map((sub, sIdx) => {
                                                            const isSubSelected = selectedSubcategory === sub;
                                                            return (
                                                                <div
                                                                    key={sIdx}
                                                                    onClick={() => handleSubcategoryClick(cat.name, sub)}
                                                                    className={`px-2 py-1 rounded-lg text-[11px] transition-all duration-200 cursor-pointer flex items-center justify-between group ${isSubSelected ? 'bg-[#F5F5F5] text-[#154734] font-bold shadow-sm' : 'text-[#333333] hover:bg-[#F5F5F5] hover:text-[#154734] font-normal'}`}
                                                                >
                                                                    <span>{sub}</span>
                                                                    <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#154734]" />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* ================= MOBILE BOTTOM NAVBAR ================= */}
            <nav className="mobile-bottom-nav !p-0 !items-stretch bg-[#154734] border-t border-[#154734]">
                <Link to="/" className={`flex flex-col items-center justify-center w-1/4 py-3 relative border-r border-white/10 ${location.pathname === '/' ? 'text-white' : 'text-white/80 hover:text-white'}`}>
                    <Home className="w-[20px] h-[20px] sm:w-5 sm:h-5 mb-1" />
                    <span className="text-[9px] sm:text-[10px] font-bold tracking-wider uppercase pb-1">Home</span>
                    {location.pathname === '/' && <span className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-[#FFF7EC]"></span>}
                </Link>
                <Link to="/departments" className={`flex flex-col items-center justify-center w-1/4 py-3 relative border-r border-white/10 ${location.pathname === '/departments' ? 'text-white' : 'text-white/80 hover:text-white'}`}>
                    <LayoutGrid className="w-[20px] h-[20px] sm:w-5 sm:h-5 mb-1" />
                    <span className="text-[9px] sm:text-[10px] font-bold tracking-wider uppercase pb-1 text-center leading-tight px-1">Departments</span>
                    {location.pathname === '/departments' && <span className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-[#FFF7EC]"></span>}
                </Link>
                <Link to="/my-lists" className={`flex flex-col items-center justify-center w-1/4 py-3 relative border-r border-white/10 ${location.pathname.startsWith('/my-lists') ? 'text-white' : 'text-white/80 hover:text-white'}`}>
                    <List className="w-[20px] h-[20px] sm:w-5 sm:h-5 mb-1" />
                    <span className="text-[9px] sm:text-[10px] font-bold tracking-wider uppercase pb-1 text-center leading-tight px-1">My List</span>
                    {location.pathname.startsWith('/my-lists') && <span className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-[#FFF7EC]"></span>}
                </Link>
                <button
                    onClick={() => {
                        if (user) {
                            navigate("/profile");
                        } else {
                            if (onAuthClick) onAuthClick('register');
                        }
                    }}
                    className={`flex flex-col items-center justify-center w-1/4 py-3 relative border-none bg-transparent cursor-pointer ${location.pathname === '/profile' ? 'text-white' : 'text-white/80 hover:text-white'}`}
                >
                    <User className="w-[20px] h-[20px] sm:w-5 sm:h-5 mb-1" />
                    <span className="text-[9px] sm:text-[10px] font-bold tracking-wider uppercase pb-1 text-center leading-tight px-1">Profile</span>
                    {location.pathname === '/profile' && <span className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-[#FFF7EC]"></span>}
                </button>
            </nav>
        </>
    );
};

export default Navbar;
